import './settings/settings-leaf.css';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, LoadingBlock, Select, TextInput } from './ui.tsx';
import { planHasFeature } from '@shared/constants/index.ts';
import type { Company, ManagedNumberDraft, ManagedSmsNumber, SessionUser } from '@shared/types/index.ts';

/**
 * One form, one button, then a status card.
 *
 * The owner says whether they have a tax ID, fills in the business, and presses
 * "Get your texting number". Everything after that — buying the number and
 * registering with the phone carriers — happens off this page. The card says
 * where it has got to, in ordinary words.
 */

type Kind = 'standard' | 'sole_proprietor';
type BusinessType = 'llc' | 'corporation' | 'partnership' | 'cooperative' | 'nonprofit';

interface FormState {
  kind: Kind;
  legalName: string;
  ein: string;
  businessType: BusinessType | '';
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  website: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  mobilePhone: string;
  areaCode: string;
}

const EMPTY_FORM: FormState = {
  kind: 'standard',
  legalName: '',
  ein: '',
  businessType: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postalCode: '',
  website: '',
  contactFirstName: '',
  contactLastName: '',
  contactEmail: '',
  contactPhone: '',
  mobilePhone: '',
  areaCode: '',
};

function formatEin(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

function splitName(fullName: string): { first: string; last: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] ?? '', last: parts.slice(1).join(' ') };
}

function formFromCompany(company: Company, user: SessionUser): FormState {
  const name = splitName(user.fullName);
  return {
    ...EMPTY_FORM,
    legalName: company.legalName || company.name,
    addressLine1: company.addressLine1 ?? '',
    addressLine2: company.addressLine2 ?? '',
    city: company.city ?? '',
    region: (company.state ?? '').toUpperCase(),
    postalCode: company.postalCode ?? '',
    website: company.website ?? '',
    contactFirstName: name.first,
    contactLastName: name.last,
    contactEmail: user.email || company.email || '',
    contactPhone: user.phone || company.phone || '',
    areaCode: '',
  };
}

function formFromDraft(draft: ManagedNumberDraft): FormState {
  return {
    kind: draft.kind,
    legalName: draft.legalName,
    ein: draft.ein ? formatEin(draft.ein) : '',
    businessType: (draft.businessType as BusinessType | null) ?? '',
    addressLine1: draft.addressLine1,
    addressLine2: draft.addressLine2 ?? '',
    city: draft.city,
    region: draft.region,
    postalCode: draft.postalCode,
    website: draft.website ?? '',
    contactFirstName: draft.contactFirstName,
    contactLastName: draft.contactLastName,
    contactEmail: draft.contactEmail,
    contactPhone: draft.contactPhone,
    mobilePhone: draft.mobilePhone ?? '',
    areaCode: draft.areaCode ?? '',
  };
}

export default function ManagedSmsNumberSection() {
  const { t } = useTranslation();
  const { can, company, user, refresh } = useSession();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const seeded = useRef(false);
  const hadDraft = useRef(false);

  const canEdit = can('company:write');
  const planGated = company !== null && !planHasFeature(company.effectivePlan, 'sms');

  const settings = useQuery({
    queryKey: ['sms-settings'],
    queryFn: () => companyApi.smsSettings(),
    enabled: canEdit && !planGated,
    refetchInterval: (query) => {
      const status = query.state.data?.managedNumber?.status;
      return status === 'pending' || status === 'provisioning' || status === 'registering' ? 30_000 : false;
    },
  });

  const activated = settings.data?.managedNumber?.status === 'active';
  const wasActivated = useRef(activated);
  useEffect(() => {
    if (wasActivated.current === activated) return;
    wasActivated.current = activated;
    void refresh();
  }, [activated, refresh]);

  useEffect(() => {
    if (!company || !user || settings.isLoading) return;
    const draft = settings.data?.managedNumber?.draft;
    if (!seeded.current) {
      setForm(draft ? formFromDraft(draft) : formFromCompany(company, user));
      seeded.current = true;
      hadDraft.current = Boolean(draft);
      return;
    }
    if (!hadDraft.current && draft) {
      setForm(formFromDraft(draft));
      hadDraft.current = true;
    }
  }, [company, user, settings.data, settings.isLoading]);

  const set = (patch: Partial<FormState>) => setForm((current) => ({ ...current, ...patch }));

  const request = useMutation({
    mutationFn: () =>
      companyApi.requestManagedNumber({
        ...(form.areaCode.trim() ? { areaCode: form.areaCode.trim() } : {}),
        registration: {
          kind: form.kind,
          legalName: form.legalName.trim(),
          ...(form.kind === 'standard'
            ? { ein: form.ein.replace(/\D/g, ''), businessType: form.businessType || undefined }
            : form.ein.replace(/\D/g, '')
              ? { ein: form.ein.replace(/\D/g, '') }
              : {}),
          addressLine1: form.addressLine1.trim(),
          ...(form.addressLine2.trim() ? { addressLine2: form.addressLine2.trim() } : {}),
          city: form.city.trim(),
          region: form.region.trim().toUpperCase(),
          postalCode: form.postalCode.trim(),
          ...(form.website.trim() ? { website: form.website.trim() } : {}),
          contactFirstName: form.contactFirstName.trim(),
          contactLastName: form.contactLastName.trim(),
          contactEmail: form.contactEmail.trim(),
          contactPhone: form.contactPhone.trim(),
          ...(form.kind === 'sole_proprietor' && !form.ein.replace(/\D/g, '')
            ? { mobilePhone: form.mobilePhone.trim() }
            : {}),
        },
      }),
    onSuccess: () => {
      setFieldErrors({});
      setMessage({ tone: 'success', text: t('managedNumber.requested') });
      void settings.refetch();
    },
    onError: (caught) => {
      if (caught instanceof ApiRequestError) {
        setFieldErrors(caught.fields);
        const unfinished = /not finished/i.test(caught.message);
        setMessage(
          Object.keys(caught.fields).length > 0
            ? null
            : { tone: 'error', text: unfinished ? t('managedNumber.setupNotFinished') : caught.message },
        );
      } else {
        setMessage({ tone: 'error', text: t('managedNumber.requestError') });
      }
    },
  });

  const resend = useMutation({
    mutationFn: () => companyApi.resendManagedNumberConfirmation(),
    onSuccess: () => {
      setMessage({ tone: 'success', text: t('managedNumber.resent') });
      void settings.refetch();
    },
    onError: (caught) => {
      const text = caught instanceof ApiRequestError ? caught.message : t('managedNumber.requestError');
      const wait = /minute/i.test(text);
      setMessage({ tone: 'error', text: wait ? t('managedNumber.resendWait') : text });
    },
  });

  if (!canEdit || planGated) return null;
  const data = settings.data;
  // FEATURE_MANAGED_SMS off: nothing new is offered. An issued number that can
  // still send (the server says so) stays visible, without a form or a resend.
  const managedSmsOff = company?.managedSmsAvailable === false;
  const keptWhileOff = managedSmsOff && data?.managedNumber?.usable === true;
  if (data && !data.managedNumbersAvailable && !keptWhileOff) return null;
  // Off, most companies have no issued number: do not flash the card while that is loading.
  if (managedSmsOff && !data) return null;

  const managed = data?.managedNumber ?? null;
  const showForm = !managedSmsOff && (!managed || managed.status === 'failed' || managed.stranded);

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    const need = (key: string, value: string, message: string) => {
      if (!value.trim()) errors[`registration.${key}`] = message;
    };
    need('legalName', form.legalName, t('managedNumber.errors.legalName'));
    need('addressLine1', form.addressLine1, t('managedNumber.errors.addressLine1'));
    need('city', form.city, t('managedNumber.errors.city'));
    if (!/^[A-Za-z]{2}$/.test(form.region.trim())) errors['registration.region'] = t('managedNumber.errors.state');
    if (!/^\d{5}(-\d{4})?$/.test(form.postalCode.trim())) {
      errors['registration.postalCode'] = t('managedNumber.errors.postalCode');
    }
    need('contactFirstName', form.contactFirstName, t('managedNumber.errors.contactFirstName'));
    need('contactLastName', form.contactLastName, t('managedNumber.errors.contactLastName'));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())) {
      errors['registration.contactEmail'] = t('managedNumber.errors.contactEmail');
    }
    need('contactPhone', form.contactPhone, t('managedNumber.errors.contactPhone'));
    if (form.areaCode.trim() && !/^\d{3}$/.test(form.areaCode.trim())) {
      errors.areaCode = t('managedNumber.errors.areaCode');
    }
    const ein = form.ein.replace(/\D/g, '');
    const hasEin = /^\d{9}$/.test(ein) && ein !== '000000000' && ein !== '123456789' && !/^(\d)\1{8}$/.test(ein);

    if (form.kind === 'standard') {
      if (!hasEin) {
        errors['registration.ein'] = /^\d{9}$/.test(ein)
          ? t('managedNumber.errors.einInvalid')
          : t('managedNumber.errors.ein');
      }
      if (!form.businessType) errors['registration.businessType'] = t('managedNumber.errors.businessType');
      if (!form.website.trim()) errors['registration.website'] = t('managedNumber.errors.website');
    } else if (hasEin) {
      if (!form.website.trim()) errors['registration.website'] = t('managedNumber.errors.website');
    } else if (!form.mobilePhone.trim()) {
      errors['registration.mobilePhone'] = t('managedNumber.errors.mobilePhone');
    }
    return errors;
  }

  const businessTypes: BusinessType[] = ['llc', 'corporation', 'partnership', 'cooperative', 'nonprofit'];

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="managed-number-heading">
      <div className="work-section-heading">
        <h2 id="managed-number-heading" className="mb-3 text-lg font-semibold">
          {managedSmsOff ? t('managedNumber.issuedHeading') : t('managedNumber.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        {managedSmsOff ? t('managedNumber.issuedDescription') : t('managedNumber.description')}
      </p>

      {settings.isLoading ? <LoadingBlock label={t('managedNumber.loading')} /> : null}

      {message ? (
        <div className="mb-4">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}

      {data && managed ? (
        <ManagedNumberStatus
          managed={managed}
          onResend={managedSmsOff ? null : () => resend.mutate()}
          resending={resend.isPending}
        />
      ) : null}

      {data && showForm ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const errors = validate();
            setFieldErrors(errors);
            if (Object.keys(errors).length > 0) return;
            request.mutate();
          }}
        >
          {data.managedNumberEligible ? null : (
            <div className="mb-4">
              <Alert tone="info">{data.managedNumberIneligibleReason}</Alert>
            </div>
          )}
          {managed ? null : (
            <div className="mb-4">
              <Alert tone="info">
                {data.activeSource === 'company'
                  ? t('managedNumber.replacesOwnAccountNote')
                  : t('managedNumber.noNumberYetNote')}
              </Alert>
            </div>
          )}

          <fieldset className="mb-4">
            <legend className="mb-2 text-sm font-semibold">{t('managedNumber.kindLegend')}</legend>
            <label className="mb-2 flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="registrationKind"
                className="mt-1"
                checked={form.kind === 'standard'}
                onChange={() => set({ kind: 'standard' })}
              />
              <span>{t('managedNumber.kindStandard')}</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="registrationKind"
                className="mt-1"
                checked={form.kind === 'sole_proprietor'}
                onChange={() => set({ kind: 'sole_proprietor' })}
              />
              <span>{t('managedNumber.kindSole')}</span>
            </label>
          </fieldset>

          <div className="mb-4 grid gap-4 sm:grid-cols-2">
            <TextInput
              label={t('managedNumber.legalName')}
              name="legalName"
              value={form.legalName}
              onChange={(event) => set({ legalName: event.target.value })}
              error={fieldErrors['registration.legalName']}
              required
            />
            {form.kind === 'standard' || form.kind === 'sole_proprietor' ? (
              <TextInput
                label={t('managedNumber.ein')}
                name="ein"
                value={form.ein}
                onChange={(event) => set({ ein: formatEin(event.target.value) })}
                hint={form.kind === 'sole_proprietor' ? t('managedNumber.einSoleHint') : t('managedNumber.einHint')}
                error={fieldErrors['registration.ein']}
                inputMode="numeric"
                required={form.kind === 'standard'}
              />
            ) : null}
            {form.kind === 'standard' ? (
              <Select
                label={t('managedNumber.businessType')}
                name="businessType"
                value={form.businessType}
                onChange={(event) => set({ businessType: event.target.value as BusinessType | '' })}
                error={fieldErrors['registration.businessType']}
                required
                options={[
                  { value: '', label: t('managedNumber.businessTypePlaceholder') },
                  ...businessTypes.map((type) => ({ value: type, label: t(`managedNumber.businessTypes.${type}`) })),
                ]}
              />
            ) : null}
            <TextInput
              label={t('managedNumber.addressLine1')}
              name="addressLine1"
              value={form.addressLine1}
              onChange={(event) => set({ addressLine1: event.target.value })}
              error={fieldErrors['registration.addressLine1']}
              required
            />
            <TextInput
              label={t('managedNumber.addressLine2')}
              name="addressLine2"
              value={form.addressLine2}
              onChange={(event) => set({ addressLine2: event.target.value })}
            />
            <TextInput
              label={t('managedNumber.city')}
              name="city"
              value={form.city}
              onChange={(event) => set({ city: event.target.value })}
              error={fieldErrors['registration.city']}
              required
            />
            <TextInput
              label={t('managedNumber.state')}
              name="region"
              value={form.region}
              onChange={(event) => set({ region: event.target.value.toUpperCase() })}
              error={fieldErrors['registration.region']}
              maxLength={2}
              required
            />
            <TextInput
              label={t('managedNumber.postalCode')}
              name="postalCode"
              value={form.postalCode}
              onChange={(event) => set({ postalCode: event.target.value })}
              error={fieldErrors['registration.postalCode']}
              required
            />
            <TextInput
              label={t('managedNumber.website')}
              name="website"
              value={form.website}
              onChange={(event) => set({ website: event.target.value })}
              hint={t('managedNumber.websiteHint')}
              error={fieldErrors['registration.website']}
            />
            <TextInput
              label={t('managedNumber.contactFirstName')}
              name="contactFirstName"
              value={form.contactFirstName}
              onChange={(event) => set({ contactFirstName: event.target.value })}
              error={fieldErrors['registration.contactFirstName']}
              required
            />
            <TextInput
              label={t('managedNumber.contactLastName')}
              name="contactLastName"
              value={form.contactLastName}
              onChange={(event) => set({ contactLastName: event.target.value })}
              error={fieldErrors['registration.contactLastName']}
              required
            />
            <TextInput
              label={t('managedNumber.contactEmail')}
              name="contactEmail"
              type="email"
              value={form.contactEmail}
              onChange={(event) => set({ contactEmail: event.target.value })}
              error={fieldErrors['registration.contactEmail']}
              required
            />
            <TextInput
              label={t('managedNumber.contactPhone')}
              name="contactPhone"
              type="tel"
              value={form.contactPhone}
              onChange={(event) => set({ contactPhone: event.target.value })}
              error={fieldErrors['registration.contactPhone']}
              required
            />
            {form.kind === 'sole_proprietor' && !/^\d{9}$/.test(form.ein.replace(/\D/g, '')) ? (
              <TextInput
                label={t('managedNumber.mobilePhone')}
                name="mobilePhone"
                type="tel"
                value={form.mobilePhone}
                onChange={(event) => set({ mobilePhone: event.target.value })}
                hint={t('managedNumber.mobileHint')}
                error={fieldErrors['registration.mobilePhone']}
                required
              />
            ) : null}
            <TextInput
              label={t('managedNumber.areaCodeLabel')}
              name="areaCode"
              value={form.areaCode}
              onChange={(event) => set({ areaCode: event.target.value.replace(/\D/g, '').slice(0, 3) })}
              hint={t('managedNumber.areaCodeHint')}
              error={fieldErrors.areaCode}
              inputMode="numeric"
              maxLength={3}
            />
          </div>

          <Button type="submit" disabled={request.isPending || !data.managedNumberEligible}>
            {request.isPending
              ? t('managedNumber.requesting')
              : managed
                ? managed.stranded
                  ? t('managedNumber.replaceCta')
                  : t('managedNumber.retryCta')
                : t('managedNumber.requestCta')}
          </Button>
        </form>
      ) : null}
    </section>
  );
}

function ManagedNumberStatus({
  managed,
  onResend,
  resending,
}: {
  managed: ManagedSmsNumber;
  /** Null hides the resend button (FEATURE_MANAGED_SMS off). */
  onResend: (() => void) | null;
  resending: boolean;
}) {
  const { t } = useTranslation();
  const tone = managed.stranded
    ? 'warning'
    : managed.status === 'active'
      ? 'success'
      : managed.status === 'failed'
        ? 'warning'
        : 'info';

  return (
    <>
      <div className="mb-4">
        <Alert
          tone={tone}
          title={
            managed.stranded ? t('managedNumber.strandedTitle') : t(`managedNumber.status.${managed.status}.title`)
          }
        >
          <p>{managed.stranded ? t('managedNumber.strandedBody') : t(`managedNumber.status.${managed.status}.body`)}</p>
          {managed.statusDetail && managed.status !== 'active' && !managed.stranded ? (
            <p className="mt-2 text-sm">{managed.statusDetail}</p>
          ) : null}
          {managed.awaitingMobileReply && onResend ? (
            <div className="mt-3">
              <Button type="button" variant="secondary" onClick={onResend} disabled={resending}>
                {resending ? t('managedNumber.resending') : t('managedNumber.resendCta')}
              </Button>
            </div>
          ) : null}
        </Alert>
      </div>

      <dl className="mb-4 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="font-semibold">{t('managedNumber.numberLabel')}</dt>
          <dd className="break-all text-[color:var(--color-muted)]">
            {managed.phoneNumber ?? t('managedNumber.numberPending')}
          </dd>
        </div>
        <div>
          <dt className="font-semibold">{t('managedNumber.requestedLabel')}</dt>
          <dd className="text-[color:var(--color-muted)]">{new Date(managed.requestedAt).toLocaleDateString()}</dd>
        </div>
      </dl>
    </>
  );
}
